import type { AncestorEvent } from '../domain/AncestorEvent';
import type { AncestorGraph } from '../domain/AncestorGraph';
import type { ChildBirth } from '../domain/ChildBirth';
import type { Person } from '../domain/Person';
import type { TimelinePoint, TimelinePointKind } from '../domain/TimelinePoint';
import { createLogger } from '../utils/logger';

const logger = createLogger('AncestorTimelineService');

type EvidenceKind = 'sibling birth' | 'child birth';

/** Sibling and child births are household evidence, not the ancestor's own events. */
export function isEvidencePoint(p: TimelinePoint): p is TimelinePoint & { kind: EvidenceKind } {
  return p.kind === 'sibling birth' || p.kind === 'child birth';
}

/** Dated ascending, undated last; stable. */
function byYear(a: { year?: number }, b: { year?: number }): number {
  if (a.year === undefined && b.year === undefined) {
    return 0;
  }
  if (a.year === undefined) {
    return 1;
  }
  if (b.year === undefined) {
    return -1;
  }
  return a.year - b.year;
}

function point(kind: TimelinePointKind, e: AncestorEvent): TimelinePoint {
  return { kind, year: e.year, location: e.location };
}

/**
 * Builds each ancestor's timeline (P-15e, owner-approved rule):
 *
 *   birth
 *   → non-ancestor siblings' births while growing up (from the ancestor's birth year to
 *     the ancestor marriage; to the next ancestor's birth year when the marriage is undated)
 *   → marriage(s) to the other ancestor (GraphBuilderService drops the rest)
 *   → the ancestor's non-ancestor children, from the marriage to the next ancestor's birth
 *   → the next ancestor's birth
 *
 * Sibling and child births must be dated to be placed in a window; the ancestor's own
 * points are always kept, so a line stays continuous across missing data. Siblings are
 * read from both parents and de-duplicated by childId; ancestors are excluded because
 * their births are their own points. Events outside this timeline — marriages to
 * non-ancestors, later children, deaths — have no point.
 */
export class AncestorTimelineService {
  build(graph: AncestorGraph): Map<string, TimelinePoint[]> {
    const ancestorChildren = new Map<string, Person[]>();
    for (const person of graph.persons.values()) {
      for (const parentId of [person.fatherId, person.motherId]) {
        if (parentId !== undefined && graph.persons.has(parentId)) {
          ancestorChildren.set(parentId, [...(ancestorChildren.get(parentId) ?? []), person]);
        }
      }
    }

    const timelines = new Map<string, TimelinePoint[]>();
    let evidence = 0;
    for (const person of graph.persons.values()) {
      const points = this._timeline(graph, person, ancestorChildren.get(person.id) ?? []);
      evidence += points.filter(isEvidencePoint).length;
      timelines.set(person.id, points);
    }
    logger.debug(`Timelines built — ${timelines.size} ancestors, ${evidence} evidence points`);
    return timelines;
  }

  private _timeline(graph: AncestorGraph, person: Person, children: Person[]): TimelinePoint[] {
    const birth = person.events.find((e) => e.type === 'birth');
    const marriages = person.events.filter((e) => e.type === 'marriage').sort(byYear);
    const marriageYear = marriages.find((m) => m.year !== undefined)?.year;
    // The next ancestor: the earliest-born ancestor child (more than one only with pedigree collapse)
    const nextBirth = children
      .map((c) => c.events.find((e) => e.type === 'birth'))
      .filter((e): e is AncestorEvent => e !== undefined)
      .sort(byYear)[0];
    const nextYear = nextBirth?.year;

    const points: TimelinePoint[] = [];
    if (birth !== undefined) {
      points.push(point('birth', birth));
    }

    const childhoodEnd = marriageYear ?? nextYear;
    if (birth?.year !== undefined && childhoodEnd !== undefined) {
      points.push(
        ...this._evidence(
          graph,
          [person.fatherId, person.motherId],
          birth.year,
          childhoodEnd,
          'sibling birth',
        ),
      );
    }

    for (const marriage of marriages) {
      points.push(point('marriage', marriage));
    }

    if (marriageYear !== undefined && nextYear !== undefined) {
      points.push(...this._evidence(graph, [person.id], marriageYear, nextYear, 'child birth'));
    }

    if (nextBirth !== undefined) {
      points.push(point('ancestor child birth', nextBirth));
    }
    return points;
  }

  /** Dated non-ancestor births of the given parents' children within [from, to]. */
  private _evidence(
    graph: AncestorGraph,
    parentIds: Array<string | undefined>,
    from: number,
    to: number,
    kind: EvidenceKind,
  ): TimelinePoint[] {
    const seen = new Set<string>();
    const births: ChildBirth[] = [];
    for (const parentId of parentIds) {
      if (parentId === undefined) {
        continue;
      }
      for (const b of graph.childBirthsByParent.get(parentId) ?? []) {
        if (seen.has(b.childId) || graph.persons.has(b.childId)) {
          continue;
        }
        seen.add(b.childId);
        if (b.year !== undefined && b.location !== undefined && b.year >= from && b.year <= to) {
          births.push(b);
        }
      }
    }
    return births.sort(byYear).map((b) => ({ kind, year: b.year, location: b.location }));
  }
}
